import { USE_PARENT_OBJECT } from '@modules/common/constants';
import {
  getCountForFieldEQLFunction,
  getCountForFieldEQLFunctionParams,
  getFieldNameFromEqlField,
  isCountForFunctionRequired,
} from '@modules/common/utils';
import { DENOMINATOR_QUERY_ALIAS } from '@modules/quantify/constants';
import { useIsAnomaliesTrendVisible } from '@modules/quantify/hooks/useIsAnomaliesTrendVisible';
import { useShouldAddDenominatorClause } from '@modules/quantify/hooks/useShouldAddDenominatorClause';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { VisualisationType } from '@modules/quantify/store/types';
import { useCallback, useRef } from 'react';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { EQLFunction, EQLFunctionName } from 'v2/core/repository/eql/types';
import { shallow } from 'zustand/shallow';

interface useAddFormulaFunctionIfRequiredParams {
  shouldFetchCount?: boolean;
  variant: 'chart' | 'table';
}

export function useAddFormulaFunctionIfRequired(
  params: useAddFormulaFunctionIfRequiredParams,
) {
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();
  const isAnomaliesTrendVisible = useIsAnomaliesTrendVisible();

  const state = useQuantifyStore(({ query, metadata, visual }) => {
    return {
      shouldShowAllValues: metadata.shouldShowAllValues,
      shouldShowTopDataPoints: metadata.shouldShowTopDataPoints,
      topDataPointsSelectionCount: metadata.topDataPointsSelectionCount,
      visibleDataPoints: metadata.visibleDataPoints,
      pageNumber: visual.pageNumber,
      compareTo: query.compareTo,
      groupBy: query.groupBy,
      activeVisualisationType: metadata.activeVisualisationType,
      anomalyThreshold: metadata.anomalyThreshold,
      formulaID: query.formulaID,
      measuredAsCriteria: query.measuredAsCriteria,
    };
  }, shallow);
  const shouldAddDenominatorClause = useShouldAddDenominatorClause();

  const dependencies = {
    ...state,
    sourceDetailsMap,
    isAnomaliesTrendVisible,
    shouldFetchCount: params.shouldFetchCount,
    variant: params.variant,
    shouldAddDenominatorClause,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  return useCallback((functions: EQLFunction[]) => {
    const formulaParams = dependenciesRef.current.measuredAsCriteria?.field
      ? [
          dependenciesRef.current.measuredAsCriteria?.field,
          ...(dependenciesRef.current.shouldFetchCount
            ? [USE_PARENT_OBJECT]
            : []),
          ...(dependenciesRef.current.shouldAddDenominatorClause
            ? [DENOMINATOR_QUERY_ALIAS]
            : []),
        ]
      : undefined;

    const getFunctionForAnomaly = () => {
      if (
        !dependenciesRef.current.isAnomaliesTrendVisible ||
        !(
          dependenciesRef.current.activeVisualisationType ===
            VisualisationType.BAR ||
          dependenciesRef.current.activeVisualisationType ===
            VisualisationType.TREND
        )
      ) {
        return null;
      }

      return {
        name: EQLFunctionName.Anomaly,
        params: ['zscore', dependenciesRef.current.anomalyThreshold, false],
      };
    };

    const getEqlFunctionName = () => {
      if (dependenciesRef.current.shouldFetchCount) {
        return EQLFunctionName.Count;
      }

      return dependenciesRef.current.formulaID;
    };

    const formulaFunctionName = getEqlFunctionName();
    const eqlFieldName = dependenciesRef.current.groupBy
      ? getFieldNameFromEqlField(dependenciesRef.current.groupBy)
      : undefined;
    const countForLabelsFunc = getCountForFieldEQLFunction(eqlFieldName);

    const shouldUseCountForFunction =
      countForLabelsFunc &&
      isCountForFunctionRequired({
        eqlFieldName,
        shouldShowAllValues: dependenciesRef.current.shouldShowAllValues,
        shouldShowTopDataPoints:
          dependenciesRef.current.shouldShowTopDataPoints,
        variant: dependenciesRef.current.variant,
        visibleDataPoints: dependenciesRef.current.visibleDataPoints,
      });

    if (dependenciesRef.current.compareTo) {
      if (shouldUseCountForFunction) {
        functions.push(
          {
            name: countForLabelsFunc,
            params: getCountForFieldEQLFunctionParams(
              dependenciesRef.current.visibleDataPoints,
              eqlFieldName,
              dependenciesRef.current.sourceDetailsMap,
            ),
          },
          {
            name: formulaFunctionName,
            params: formulaParams,
          },
        );
      } else {
        functions.push({
          name: formulaFunctionName,
          params: formulaParams,
        });
      }
      return;
    }

    const funcAnomaly = getFunctionForAnomaly();

    if (shouldUseCountForFunction) {
      const funcCountFor = {
        name: countForLabelsFunc,
        params: getCountForFieldEQLFunctionParams(
          dependenciesRef.current.visibleDataPoints,
          eqlFieldName,
          dependenciesRef.current.sourceDetailsMap,
        ),
      };

      if (funcAnomaly) {
        functions.push(funcAnomaly, funcCountFor);
      } else {
        functions.push(funcCountFor);
      }

      functions.push({
        name: formulaFunctionName,
        params: formulaParams,
      });

      return;
    }

    if (funcAnomaly) {
      functions.push(funcAnomaly, {
        name: formulaFunctionName,
        params: formulaParams,
      });
    } else {
      functions.push({
        name: formulaFunctionName,
        params: formulaParams,
      });
    }
  }, []);
}
